import styled from '@emotion/styled';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CoachingVisitNode } from 'src/generated/webapp_gql';
import noSessionIcon from 'src/public/img/generic-schedule.svg';
import { Icon, Icons } from 'src/shared/ds/components/Icon';
import { dsColors, DsColors } from 'src/shared/ds/variables';
import { color, font } from 'src/shared/styles/variables';

interface Props {
    coachAssignmentUuid: string;
    completedResponses: number;
    emptyResponses: number;
    nextSession?: CoachingVisitNode;
    wrapped?: boolean;
    navigateByCoachAssignment: (coachAssignmentUuid: string) => void;
    endIconName?: Icons;
    endIconColor?: DsColors;
}

interface WrapperProps {
    wrapped?: boolean;
}

export enum IntakeFormReminderButtonTestID {
    Wrapper = 'IntakeFormReminderButtonTestID.Wrapper',
    NoSession = 'IntakeFormReminderButtonTestID.NoSession',
    Calendar = 'IntakeFormReminderButtonTestID.Calendar',
}

const ReminderButtonWrapper = styled('div')<WrapperProps>(({ wrapped }) => ({
    padding: 10,
    backgroundColor: color.white,
    [wrapped ? 'borderBottom' : 'border']: `2px solid ${color.catskillWhite}`,
    display: 'flex',
    cursor: 'pointer',
    justifyContent: 'flex-start',
    alignItems: 'center',
}));

export const CalendarIconContainer = styled.div({
    minWidth: 40,
    minHeight: 40,
    display: 'flex',
    flexDirection: 'column',
});

export const CalendarIconMonth = styled.div({
    width: '100%',
    textAlign: 'center',
    paddingTop: 2,
    borderRadius: '4px 4px 0px 0px',
    backgroundColor: color.royalBlueSaturated,
    color: color.white,
    textTransform: 'capitalize',
    ...font.black(font.size.xxSmall),
});

export const CalendarIconDay = styled.div({
    width: '100%',
    textAlign: 'center',
    borderRadius: '0px 0px 4px 4px',
    backgroundColor: color.concrete,
    padding: 2,
    color: color.nightRider,
    ...font.heavy(font.size.base),
});

export const NoSessionIcon = styled.div({
    backgroundImage: `url(${noSessionIcon})`,
    height: 40,
    width: 40,
});

const IntakeFormInfoText = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 16,
    alignItems: 'flex-start',
});

const InfoTextTitle = styled.div({
    color: color.shark,
    ...font.medium(font.size.medium),
    letterSpacing: 0.7,
    fontWeight: 900,
});

const InfoTextSubtitle = styled.div({
    color: color.steelGray,
    ...font.medium(font.size.base),
    letterSpacing: 0.2,
});

export const IntakeFormReminderButton: React.FC<Props> = ({
    nextSession,
    emptyResponses,
    completedResponses,
    coachAssignmentUuid,
    wrapped,
    navigateByCoachAssignment,
    endIconName = 'chevron-right',
    endIconColor = dsColors.darkGrey,
}) => {
    const { t } = useTranslation();
    const warningOptions = {
        0: t('intakeForms:reminder_button.review_form'),
        1: t('intakeForms:reminder_button.last_question'),
        2: t('intakeForms:reminder_button.almost_there', { emptyResponses }),
    };
    let nextScheduledSession;

    const handleNavigate = (event: React.SyntheticEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        event.preventDefault();

        navigateByCoachAssignment(coachAssignmentUuid);
    };

    const handleNavigateWithEnter = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();

            handleNavigate(event);
        }
    };

    if (nextSession) {
        nextScheduledSession = moment(nextSession.scheduledTime);
    }

    if (!coachAssignmentUuid || !(completedResponses || emptyResponses)) {
        return null;
    }

    return (
        <ReminderButtonWrapper
            data-testid={IntakeFormReminderButtonTestID.Wrapper}
            data-href={`/care-form/${coachAssignmentUuid}`}
            tabIndex={0}
            role="link"
            onClick={handleNavigate}
            onKeyDown={handleNavigateWithEnter}
            wrapped={wrapped}
        >
            {nextScheduledSession && (
                <CalendarIconContainer data-testid={IntakeFormReminderButtonTestID.Calendar}>
                    <CalendarIconMonth>{nextScheduledSession.format('MMM')}</CalendarIconMonth>
                    <CalendarIconDay>{nextScheduledSession.format('D')}</CalendarIconDay>
                </CalendarIconContainer>
            )}
            {!nextScheduledSession && (
                <NoSessionIcon data-testid={IntakeFormReminderButtonTestID.NoSession} />
            )}
            {!!completedResponses && (
                <IntakeFormInfoText>
                    <InfoTextTitle>{t('intakeForms:reminder_button.finish_form')}</InfoTextTitle>
                    <InfoTextSubtitle>
                        {warningOptions[[0, 1].includes(emptyResponses) ? emptyResponses : 2]}
                    </InfoTextSubtitle>
                </IntakeFormInfoText>
            )}
            {!completedResponses && (
                <IntakeFormInfoText>
                    <InfoTextTitle>{t('intakeForms:reminder_button.start_form')}</InfoTextTitle>
                    <InfoTextSubtitle>
                        {t('intakeForms:reminder_button.setup_for_success')}
                    </InfoTextSubtitle>
                </IntakeFormInfoText>
            )}
            <Icon name={endIconName} color={endIconColor} size={20} />
        </ReminderButtonWrapper>
    );
};
